// @flow
import queryString from 'query-string';
import { getJson, putJson, postJson, patchJson, del } from 'utils/http';
import type { IdealistDayEvent as GroupIdealistDayEvent } from 'api/groupAdmin';
import type { OrgMini, ListingMini } from 'api/orgs';

export type EventStatus =
  | 'APPROVED'
  | 'PENDING'
  | 'REJECTED'
  | 'ARCHIVED'
  | 'DELETED'
  | 'DRAFT'
  | 'FROZEN'
  | 'HIDDEN'
  | 'PUBLISHED'
  | 'PULLED';

export type IdealistDayEvent = {
  ...$Exact<GroupIdealistDayEvent>,
  status: $ReadOnlyArray<EventStatus>,
  isApproved: ?boolean
};

export type Image = {
  filename: string,
  handle: string,
  mimetype: string,
  size: number,
  source: string
};

export type UserInfo = {
  id: string,
  created: number,
  updated: number,
  firstName: ?string,
  lastName: ?string,
  email: ?string,
  emailVerified: boolean,
  hasPassword: boolean,
  locale: string,
  hasPaymentInfo: boolean,
  locationText: string,
  stateCode: string,
  countryCode: string,
  latitude: number,
  longitude: number,
  timezone: string,
  profile: ?{
    description: string,
    links: Array<string>,
    image: ?Image
  },
  isLastOwnerOfOrgs: Array<{
    id: string,
    name: string,
    hasOutstandingInvoices: boolean,
    credits: number
  }>,
  isLastOwnerOfGroups: Array<{
    id: string,
    name: string
  }>,
  hasEvents: boolean,
  canExportData: boolean
};

export type SavedListing = {
  id: string,
  type: string,
  listing: {
    id: string,
    name: string,
    description: string,
    addressCity: string,
    addressState: string,
    addressCountry: string,
    url: { [string]: string }
  }
};

export type SavedSearch = {
  id: string,
  text: string,
  created: string,
  filters: Object,
  location: {
    location: ?string,
    hasGeo: boolean,
    geoType: ?string,
    stateCode: ?string,
    countryCode: ?string,
    latitude: ?number,
    longitude: ?number,
    boundingBoxN: ?number,
    boundingBoxS: ?number,
    boundingBoxW: ?number,
    boundingBoxE: ?number
  },
  radius: number
};

export type ListingApplication = {
  created: string,
  id: string,
  listing: ListingMini,
  org: OrgMini
};

export type GeneralUserInfo = {
  user: UserInfo,
  savedListings: Array<SavedListing>,
  savedSearches: Array<SavedSearch>,
  authProviders: Array<string>,
  idealistDayEvents: Array<IdealistDayEvent>
};

export type NotificationPreferences = {
  updatedByUser?: ?number,
  receiveEmails: ?boolean,
  receiveEmailsExplicitlyChosen: ?boolean
};

export type PaymentMethod = {
  id: string,
  brand: string,
  last4: string,
  name: string,
  type: string,
  onPendingPayment: false
};

export type WorkHistoryJob = {
  id: string,
  synonymId: ?string,
  text: string,
  orgName: ?string,
  orgId: ?string,
  isCurrent: boolean,
  startDate: ?string,
  endDate: ?string,
  positionType: 'JOB' | 'INTERNSHIP' | 'VOLUNTEER',
  isFullTime: boolean,
  managerialStatus: boolean
};

export type Location = { ...SearchLocation, full: string };

export type ApplicantProfile = {
  willingToRelocate: ?boolean,
  prefersRemote: ?boolean,
  openToRemote: ?boolean,
  location: ?Location,
  skills: ?Array<string>,
  workHistoryJobs: Array<WorkHistoryJob>,
  updatedByUser: string
};

export function fetchEvent(eventId: string): Promise<IdealistDayEvent> {
  return getJson(`/data/userdashboard/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent: event }) => event
  );
}

export function createEvent(eventInfo: Object): Promise<IdealistDayEvent> {
  return postJson(`/data/userdashboard/idealist-day-events`, eventInfo).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function updateEvent(
  eventId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return putJson(
    `/data/userdashboard/idealist-day-events/${eventId}`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function publishEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/post`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function hideEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/hide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function unhideEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/unhide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function deleteEvent(eventId: string) {
  return del(`/data/userdashboard/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function fetchUserEvents(): Promise<Array<IdealistDayEvent>> {
  return getJson(`/data/userdashboard/idealist-day-events`).then(
    ({ events }) => events
  );
}

export function getGeneralUserInfo(): Promise<GeneralUserInfo> {
  return getJson('/data/userdashboard/general-settings').then(data => ({
    user: {
      id: data.id,
      created: data.created,
      updated: data.updated,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      emailVerified: data.emailVerified,
      hasPassword: data.hasPassword,
      locale: data.locale,
      hasPaymentInfo: data.hasPaymentInfo,
      stateCode: data.stateCode,
      countryCode: data.countryCode,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      profile: data.profile,
      isLastOwnerOfOrgs: data.isLastOwnerOfOrgs,
      isLastOwnerOfGroups: data.isLastOwnerOfGroups,
      hasEvents: data.hasEvents,
      canExportData: data.canExportData
    },
    savedListings: data.savedListings,
    savedSearches: data.savedSearches,
    authProviders: data.authProviders
  }));
}

export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return getJson(`/data/userdashboard/notification-preferences`).then(
    ({ notificationPreferences }) => notificationPreferences
  );
}

export function updateNotificationPreferences(
  preferences: NotificationPreferences
): Promise<NotificationPreferences> {
  return putJson(
    `/data/userdashboard/notification-preferences`,
    preferences
  ).then(({ notificationPreferences }) => notificationPreferences);
}

export function getPaymentMethods(): Promise<Array<PaymentMethod>> {
  return getJson('/data/payment-methods').then(({ data }) => data);
}

export function addPaymentMethod(token: string): Promise<PaymentMethod> {
  return postJson(`/data/userdashboard/payment-methods`, {
    token
  }).then(({ paymentMethod }) => paymentMethod);
}

export function removePaymentMethod(methodId: string): Promise<void> {
  return del(`/data/userdashboard/payment-methods/${methodId}`);
}

export function fetchSavedListings(): Promise<Array<Object>> {
  return getJson('/data/userdashboard/saved-listings').then(
    ({ savedListings }) => savedListings
  );
}

export const fetchListingApplications = (params: { page: ?number }) => {
  const qs = queryString.stringify(params);
  return getJson(`/data/listing-applications?${qs}`).then(
    ({ listingApplications }) => listingApplications
  );
};

type ApplicantProfileResponse = {
  resumeS3FileId: ?string,
  isRecruitable: ?boolean,
  applicantProfile: ?ApplicantProfile
};

const emptyProfile = {
  resumeId: null,
  isRecruitable: null,
  workHistoryJobs: []
};

const mapProfileResponse = ({ applicantProfile }) => {
  const { resumeS3FileId: resumeId, isRecruitable, ...profile } =
    applicantProfile || emptyProfile;
  return {
    resumeId,
    isRecruitable,
    applicantProfile: profile
  };
};

export const fetchApplicantProfile = (): Promise<ApplicantProfileResponse> =>
  getJson('/data/userdashboard/applicant-profile/profile').then(
    mapProfileResponse
  );

export const updateApplicantProfile = (
  values: Object
): Promise<ApplicantProfileResponse> =>
  patchJson('/data/userdashboard/applicant-profile/profile', values).then(
    mapProfileResponse
  );

export const addWorkHistoryJob = (
  job: WorkHistoryJob
): Promise<ApplicantProfileResponse> =>
  postJson('/data/userdashboard/applicant-profile/work-history/jobs', job).then(
    mapProfileResponse
  );

export const editWorkHistoryJob = (
  id: string,
  job: WorkHistoryJob
): Promise<ApplicantProfileResponse> =>
  putJson(
    `/data/userdashboard/applicant-profile/work-history/jobs/${id}`,
    job
  ).then(mapProfileResponse);

export const deleteWorkHistoryJob = (
  id: string
): Promise<ApplicantProfileResponse> =>
  del(`/data/userdashboard/applicant-profile/work-history/jobs/${id}`).then(
    mapProfileResponse
  );
